import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Sparkle, ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { supplierApi } from "@/lib/supplierPortalApi";
import { useSupplierAuth } from "@/contexts/SupplierAuthContext";
import { SupplierPortalLayout } from "./SupplierPortalLayout";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NMRsPage() {
  const { account } = useSupplierAuth();
  const { vendorCode } = useParams();
  const isImpersonating = !!(account?.testing_mode && vendorCode && vendorCode !== account?.vendor_code);

  const [nmrs, setNmrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ root_cause: "", corrective: "", preventive: "", target_date: "", responsible_person: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [scoring, setScoring] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.get("/qms/nmrs", { params });
      setNmrs(data.nmrs || []);
    } catch (err) {
      setLoadError(err.response?.data?.detail || "Could not load NMRs from QMS.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode]);

  const openDetail = async (nmrId) => {
    try {
      const params = isImpersonating ? { as_vendor: vendorCode } : {};
      const { data } = await supplierApi.get(`/qms/nmrs/${nmrId}`, { params });
      setDetail(data);
      setForm({ root_cause: "", corrective: "", preventive: "", target_date: "", responsible_person: "" });
      setScoreResult(null);
      setSubmitError("");
    } catch (err) {
      toast.error("Could not open this NMR", { description: err.response?.data?.detail });
    }
  };

  const checkCap = async () => {
    if (!(form.root_cause.trim() && form.corrective.trim() && form.preventive.trim())) {
      setSubmitError("Fill root cause, corrective and preventive action first.");
      return;
    }
    setScoring(true);
    setSubmitError("");
    try {
      const { data } = await supplierApi.post(`/qms/nmrs/${detail.id || detail.nmr_id}/cap/score`, {
        root_cause: form.root_cause, corrective: form.corrective, preventive: form.preventive,
      });
      setScoreResult(data);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Could not score this CAP right now.");
    } finally {
      setScoring(false);
    }
  };

  const submitCap = async () => {
    if (!(form.root_cause.trim() && form.corrective.trim() && form.preventive.trim() && form.target_date && form.responsible_person.trim())) {
      setSubmitError("All CAP fields are required.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await supplierApi.post(`/qms/nmrs/${detail.id || detail.nmr_id}/cap`, form);
      toast.success("Corrective Action Plan submitted to QMS");
      setDetail(null);
      load();
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Could not submit this CAP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const viewInQms = () => {
    window.location.href = `${BACKEND_URL}/api/supplier-portal/qms/sso-redirect?next=${encodeURIComponent(`/vendor/nmrs/${detail.id || detail.nmr_id}`)}`;
  };

  return (
    <SupplierPortalLayout active="nmrs" vendorCode={vendorCode} isImpersonating={isImpersonating} pageTitle="NMRs" pageSubtitle="Non-Conformance Reports, live from QMS">
      {loadError && <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-sm rounded-lg px-4 py-3 mb-4" data-testid="nmr-load-error">{loadError}</div>}
      <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto" data-testid="nmr-table">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-[#F1F5F9] text-[#334155] text-xs font-bold font-heading uppercase tracking-wide">
            <tr>
              <th className="border border-[#E2E8F0] p-2 text-left">NMR #</th>
              <th className="border border-[#E2E8F0] p-2 text-left">Finding</th>
              <th className="border border-[#E2E8F0] p-2 text-left">Status</th>
              <th className="border border-[#E2E8F0] p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && nmrs.length === 0 && (
              <tr><td colSpan={4} className="border border-[#E2E8F0] px-2 py-6 text-center text-slate-500">No NMRs found.</td></tr>
            )}
            {nmrs.map((n) => {
              const id = n.id || n.nmr_id;
              return (
                <tr key={id} className="bg-white odd:bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors duration-150" data-testid={`nmr-row-${id}`}>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 font-data font-semibold text-[#1E40AF]">{id}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 max-w-xs">{n.finding || n.description}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5">{n.status}</td>
                  <td className="border border-[#E2E8F0] px-2 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => openDetail(id)} data-testid={`nmr-view-button-${id}`}>View</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-lg max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="nmr-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">NMR {detail?.id || detail?.nmr_id}</DialogTitle>
            <DialogDescription>{detail?.finding || detail?.description}</DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="rounded-md text-xs" onClick={viewInQms} data-testid="nmr-view-in-qms-button">
              <ArrowSquareOut size={14} className="mr-1.5" /> View full details in QMS
            </Button>
          </div>

          {detail?.cap_submitted_at ? (
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-sm text-[#065F46] flex items-center gap-2" data-testid="nmr-cap-already-submitted">
              <CheckCircle size={16} weight="fill" /> CAP already submitted.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Root Cause</label>
                <Textarea value={form.root_cause} onChange={(e) => setForm((f) => ({ ...f, root_cause: e.target.value }))} className="mt-1 rounded-md" data-testid="nmr-cap-root-cause-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Corrective Action</label>
                <Textarea value={form.corrective} onChange={(e) => setForm((f) => ({ ...f, corrective: e.target.value }))} className="mt-1 rounded-md" data-testid="nmr-cap-corrective-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Preventive Action</label>
                <Textarea value={form.preventive} onChange={(e) => setForm((f) => ({ ...f, preventive: e.target.value }))} className="mt-1 rounded-md" data-testid="nmr-cap-preventive-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Responsible Person</label>
                  <Input value={form.responsible_person} onChange={(e) => setForm((f) => ({ ...f, responsible_person: e.target.value }))} className="mt-1 rounded-md" data-testid="nmr-cap-responsible-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Target Date</label>
                  <Input type="date" value={form.target_date} onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))} className="mt-1 rounded-md" data-testid="nmr-cap-target-date-input" />
                </div>
              </div>

              <Button variant="outline" size="sm" className="rounded-md text-xs" onClick={checkCap} disabled={scoring} data-testid="nmr-check-cap-button">
                <Sparkle size={14} className="mr-1.5" /> {scoring ? "Checking..." : "Check my CAP"}
              </Button>
              {scoreResult && (
                <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-3 text-sm text-[#1E40AF]" data-testid="nmr-cap-score-result">
                  {scoreResult.feedback || scoreResult.summary || JSON.stringify(scoreResult)}
                </div>
              )}

              {submitError && <div className="text-sm text-[#991B1B]" data-testid="nmr-cap-submit-error">{submitError}</div>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-md" onClick={() => setDetail(null)}>Cancel</Button>
                <Button onClick={submitCap} disabled={submitting} className="rounded-md bg-[#1E40AF] hover:bg-[#1E3A8A]" data-testid="nmr-cap-submit-button">
                  {submitting ? "Submitting..." : "Submit CAP"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SupplierPortalLayout>
  );
}
